import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { hoyISO } from "@/lib/datetime";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Plus, Warning, CheckCircle, X } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useAuth, canSupervise } from "@/contexts/AuthContext";
import GuidedTour from "@/components/GuidedTour";
import TourHelpButton from "@/components/TourHelpButton";
import { useGuidedTour } from "@/hooks/useGuidedTour";

const TOUR_STORAGE_KEY = "chkr_tour_incidencias_seen";

function tourSteps(user) {
  return [
    { target: "[data-tour='inc-nueva']", title: "Registrar una incidencia", content: "Retardos, faltas, permisos, incapacidades, vacaciones u horas extra: selecciona el tipo y el periodo correspondiente." },
    { target: "[data-tour='inc-filtro']", title: "Filtra por estatus", content: "Revisa todas las incidencias o solo las pendientes, autorizadas o rechazadas." },
    canSupervise(user)
      ? { target: "[data-tour='inc-tabla']", title: "Autoriza o rechaza", content: "En las incidencias pendientes verás botones para aprobar o rechazar directamente desde la tabla." }
      : { target: "[data-tour='inc-tabla']", title: "Seguimiento", content: "Aquí puedes ver el estatus de cada incidencia que has registrado: pendiente, autorizada o rechazada." },
  ];
}

const TIPOS = [
  "retardo", "falta", "omision_entrada", "omision_salida", "permiso_goce",
  "permiso_sin_goce", "incapacidad", "vacaciones", "descanso_trabajado",
  "prima_dominical", "cambio_turno", "salida_anticipada", "trabajo_remoto",
  "comision", "horas_extra",
];

const STATUS_COLOR = {
  pendiente: "bg-amber-100 text-amber-800",
  autorizada: "bg-emerald-100 text-emerald-800",
  rechazada: "bg-red-100 text-red-800",
  aplicada: "bg-blue-100 text-blue-800",
};

export default function Incidencias() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [empleados, setEmpleados] = useState([]);
  const [filtroEstatus, setFiltroEstatus] = useState("todos");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ empleado_id: "", tipo: "permiso_goce", fecha_inicio: "", fecha_fin: "", descripcion: "" });
  const [busy, setBusy] = useState(false);
  const { active: tourActive, start: startTour, finish: finishTour } = useGuidedTour(TOUR_STORAGE_KEY);

  const load = async () => {
    const p = new URLSearchParams();
    if (filtroEstatus !== "todos") p.set("estatus", filtroEstatus);
    const [i, e] = await Promise.all([
      api.get("/incidencias?" + p.toString()),
      user.role === "empleado" ? Promise.resolve({ data: [] }) : api.get("/empleados"),
    ]);
    setItems(i.data);
    setEmpleados(e.data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filtroEstatus]);

  const empName = (id) => empleados.find((e) => e.id === id)?.nombre || (user?.empleado_id === id ? user.name : id);

  const startCreate = () => {
    setForm({
      empleado_id: user.role === "empleado" ? user.empleado_id : empleados[0]?.id || "",
      tipo: "permiso_goce",
      fecha_inicio: hoyISO(),
      fecha_fin: "",
      descripcion: "",
    });
    setOpen(true);
  };

  const submit = async () => {
    setBusy(true);
    try {
      await api.post("/incidencias", form);
      toast.success("Incidencia creada");
      setOpen(false); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const decidir = async (id, decision) => {
    try {
      await api.post(`/incidencias/${id}/decidir`, { decision });
      toast.success(`Incidencia ${decision}`);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="p-6 md:p-10" data-testid="incidencias-page">
      <PageHeader
        kicker="Workflow"
        title="Incidencias"
        subtitle="Retardos, faltas, permisos, incapacidades, vacaciones, horas extra y más."
        actions={
          <div className="flex gap-2 items-center">
            <Button onClick={startCreate} className="bg-brand hover:bg-brand-hover" data-testid="inc-new-btn" data-tour="inc-nueva">
              <Plus size={16} weight="bold" className="mr-2" /> Nueva incidencia
            </Button>
            <TourHelpButton onClick={startTour} />
          </div>
        }
      />

      <GuidedTour steps={tourSteps(user)} active={tourActive} onFinish={finishTour} />

      <div className="flex gap-3 mb-4" data-tour="inc-filtro">
        <Select value={filtroEstatus} onValueChange={setFiltroEstatus}>
          <SelectTrigger className="w-48"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="todos">Todos</SelectItem>
            <SelectItem value="pendiente">Pendientes</SelectItem>
            <SelectItem value="autorizada">Autorizadas</SelectItem>
            <SelectItem value="rechazada">Rechazadas</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="bg-white border border-slate-200 rounded-md overflow-hidden" data-tour="inc-tabla">
        <div className="overflow-x-auto">
        <table className="data-table">
          <thead>
            <tr>
              <th>Empleado</th>
              <th>Tipo</th>
              <th>Periodo</th>
              <th>Descripción</th>
              <th>Estatus</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && <tr><td colSpan={6} className="text-center text-slate-500 py-8">Sin incidencias</td></tr>}
            {items.map((i) => (
              <tr key={i.id} data-testid={`inc-row-${i.id}`}>
                <td className="font-semibold">{empName(i.empleado_id)}</td>
                <td>
                  <span className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-slate-700">
                    <Warning size={12} weight="duotone" />
                    {i.tipo.replace(/_/g, " ")}
                  </span>
                </td>
                <td className="font-mono text-xs">{i.fecha_inicio}{i.fecha_fin && i.fecha_fin !== i.fecha_inicio ? ` → ${i.fecha_fin}` : ""}</td>
                <td className="text-sm text-slate-600 max-w-xs truncate">{i.descripcion}</td>
                <td><span className={`text-xs px-2 py-1 rounded ${STATUS_COLOR[i.estatus]}`}>{i.estatus}</span></td>
                <td>
                  {i.estatus === "pendiente" && canSupervise(user) && (
                    <div className="flex gap-1">
                      <Button size="sm" variant="ghost" className="text-emerald-700 hover:bg-emerald-50" onClick={() => decidir(i.id, "autorizada")} data-testid={`inc-approve-${i.id}`}>
                        <CheckCircle size={16} weight="duotone" />
                      </Button>
                      <Button size="sm" variant="ghost" className="text-red-700 hover:bg-red-50" onClick={() => decidir(i.id, "rechazada")} data-testid={`inc-reject-${i.id}`}>
                        <X size={16} weight="bold" />
                      </Button>
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="inc-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">Nueva incidencia</DialogTitle>
          </DialogHeader>
          <div className="grid gap-4">
            {user.role !== "empleado" && (
              <div className="space-y-1">
                <Label className="label-uppercase">Empleado</Label>
                <Select value={form.empleado_id} onValueChange={(v) => setForm({ ...form, empleado_id: v })}>
                  <SelectTrigger><SelectValue placeholder="Selecciona empleado" /></SelectTrigger>
                  <SelectContent>{empleados.map((e) => <SelectItem key={e.id} value={e.id}>{e.nombre}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            )}
            <div className="space-y-1">
              <Label className="label-uppercase">Tipo</Label>
              <Select value={form.tipo} onValueChange={(v) => setForm({ ...form, tipo: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{TIPOS.map((t) => <SelectItem key={t} value={t} className="capitalize">{t.replace(/_/g, " ")}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-1"><Label className="label-uppercase">Inicio</Label><Input type="date" value={form.fecha_inicio} onChange={(e) => setForm({ ...form, fecha_inicio: e.target.value })} /></div>
              <div className="space-y-1"><Label className="label-uppercase">Fin</Label><Input type="date" value={form.fecha_fin || ""} onChange={(e) => setForm({ ...form, fecha_fin: e.target.value })} /></div>
            </div>
            <div className="space-y-1">
              <Label className="label-uppercase">Descripción</Label>
              <Input value={form.descripcion} onChange={(e) => setForm({ ...form, descripcion: e.target.value })} placeholder="Motivo o referencia" data-testid="inc-desc-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
            <Button onClick={submit} disabled={busy} className="bg-brand hover:bg-brand-hover" data-testid="inc-save-btn">
              {busy ? "Guardando…" : "Crear"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
